/**
 * El asistente de una membresía o un paquete de clases.
 *
 * El mismo marco que el de las demás ofertas (`NewOfferWizard`): barra con el
 * raíl de pasos, el formulario a la izquierda y la vista previa a la derecha.
 * Los pasos son los suyos: qué es, cuánto cuesta y qué da, qué clases incluye,
 * las ventajas de miembro y el repaso. Sirve para crearla y para editarla
 * (desde «Ajustes» de su ficha, como cualquier oferta).
 *
 * Una membresía ES una oferta (tipo servicio, con precio recurrente); un
 * paquete, una oferta de pago único. Lo propio —reservas por periodo, clases,
 * vigencia, descuento de miembro— se guarda aparte con `saveMembership`.
 */
import { matchesSearch } from '@webbuilder/shared-types'
import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  IconArrowLeft,
  IconArrowRight,
  IconCalendarEvent,
  IconDownload,
  IconCheck,
  IconCoin,
  IconDiscount,
  IconEye,
  IconInfoCircle,
  IconLoader2,
  IconPencil,
  IconRepeat,
  IconSearch,
  IconTag,
  IconTicket,
  IconWorld,
} from '@tabler/icons-react'
import { WizardTopBar } from '../WizardTopBar'
import { ConfirmDialog } from '../ConfirmDialog'
import { PendingChangesBar } from '../../DashboardTopbarActions'
import { useI18n, translateServerError } from '../../../lib/i18n'
import { useRegion } from '../../../lib/region'
import { useScrollTopOnStep } from '../../../lib/useScrollTopOnStep'
import { useToast } from '../../Toast'
import { CurrencySymbol } from './CurrencySymbol'
import { OfferGalleryField } from './OfferGalleryField'
import { CADENCES, formatMoney, minorUnitFactor, toMinorUnits, type CadenceId } from './offerShared'
import {
  addOfferPrice,
  createOffer,
  removeOfferPrice,
  saveMembership,
  updateOffer,
  type MembershipItem,
  type MembershipsCatalog,
  type OfferPrice,
} from '../../../lib/api'

/* Las cadencias de una membresía, de la más corta a la más larga. */
const MEMBERSHIP_CADENCES: CadenceId[] = ['week', 'biweek', 'month', 'quarter', 'half', 'year']
const cadenceOf = (id: CadenceId) => CADENCES.find((row) => row.id === id) ?? CADENCES[0]
type StepKey = 'info' | 'access' | 'benefits' | 'review'

const ALL_STEPS: Array<{ key: StepKey; labelKey: string; Icon: typeof IconInfoCircle }> = [
  { key: 'info', labelKey: 'memberships.wizard.step.info', Icon: IconInfoCircle },
  { key: 'access', labelKey: 'memberships.wizard.step.access', Icon: IconCoin },
  { key: 'benefits', labelKey: 'memberships.wizard.step.benefits', Icon: IconCalendarEvent },
  { key: 'review', labelKey: 'memberships.wizard.step.review', Icon: IconEye },
]

export function MembershipWizard({ projectId, token, catalog, item, onClose, onSaved }: {
  projectId: string
  token: string
  catalog: MembershipsCatalog
  /** Nulo = nueva. */
  item: MembershipItem | null
  onClose: () => void
  onSaved: (offerId: string) => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const { currency: regionCurrency } = useRegion()
  const editing = Boolean(item)
  const currency = item?.price?.currency ?? regionCurrency
  const factor = minorUnitFactor(currency)

  const [kind, setKind] = useState<'membership' | 'pack'>(item?.kind ?? 'membership')
  const [title, setTitle] = useState(item?.title ?? '')
  const [description, setDescription] = useState(item?.shortDescription ?? '')
  const [images, setImages] = useState<string[]>(item?.images?.length ? item.images : item?.image ? [item.image] : [])
  const image = images[0] ?? ''
  const [amount, setAmount] = useState(item?.price ? String(item.price.amount / factor) : '')
  const [cadence, setCadence] = useState<CadenceId>(() => {
    const price = item?.price
    const found = price ? CADENCES.find((row) => row.interval === price.interval && row.count === (price.intervalCount || 1)) : undefined
    return found?.id ?? 'month'
  })
  const chosenCadence = cadenceOf(cadence)
  const [unlimited, setUnlimited] = useState(item ? item.bookingsPerPeriod === null : true)
  const [perPeriod, setPerPeriod] = useState(String(item?.bookingsPerPeriod ?? 8))
  const [credits, setCredits] = useState(String(item?.packCredits ?? 10))
  const [expires, setExpires] = useState(Boolean(item?.packValidDays))
  const [validDays, setValidDays] = useState(String(item?.packValidDays ?? 90))
  const [includes, setIncludes] = useState<string[]>(item?.includes ?? [])
  const [query, setQuery] = useState('')
  const [discountOn, setDiscountOn] = useState(Boolean(item?.memberDiscountPercent))
  const [discountPercent, setDiscountPercent] = useState(String(item?.memberDiscountPercent ?? 10))
  const [discountScope, setDiscountScope] = useState<'all' | 'some'>(item?.memberDiscountOfferIds?.length ? 'some' : 'all')
  const [discountIds, setDiscountIds] = useState<string[]>(item?.memberDiscountOfferIds ?? [])
  const [discountQuery, setDiscountQuery] = useState('')
  /* Productos digitales que da (como Kajabi): mientras la membresía esté
     activa, o durante la vigencia del paquete. Migración 0056. */
  const [digitalOn, setDigitalOn] = useState((item?.digitalIncludes?.length ?? 0) > 0)
  const [digitalIds, setDigitalIds] = useState<string[]>(item?.digitalIncludes ?? [])
  const [digitalQuery, setDigitalQuery] = useState('')
  const [publish, setPublish] = useState(item ? item.status === 'active' : true)
  /* Publicar también las clases incluidas que siguen en borrador. Marcado:
     lo normal es querer que los miembros puedan reservarlas ya. */
  const [publishDrafts, setPublishDrafts] = useState(true)
  const [step, setStep] = useState<StepKey>('info')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [invalid, setInvalid] = useState<'title' | 'amount' | 'rule' | 'benefits' | 'classes' | 'credits' | 'digital' | 'discount' | null>(null)
  /* Qué da: clases incluidas, descuento, o las dos. Al menos una. */
  const [classesOn, setClassesOn] = useState(item ? item.includes.length > 0 : catalog.bookings.length > 0)
  /*
   * Gratis: un club (unirse con la cuenta) que sólo da descuento, p. ej. un 5 %.
   * Sin clases incluidas: gratis y con reservas incluidas serían clases gratis
   * sin límite. Necesita el descuento de miembro (migración 0055).
   */
  const [whyNoClasses, setWhyNoClasses] = useState(false)
  const [whyKindLocked, setWhyKindLocked] = useState(false)
  /* El tipo se cambia mientras nadie la tenga: con miembros o compras, pasar
     de membresía a paquete (o al revés) dejaría suscripciones o clases
     compradas colgando. */
  const kindLocked = Boolean(item && (item.activeMembers > 0 || (item.purchases ?? 0) > 0))
  const [free, setFree] = useState(Boolean(item && item.kind === 'membership' && item.price?.amount === 0))

  const shape = JSON.stringify({
    kind, title, description, images, amount, cadence, unlimited, perPeriod,
    credits, expires, validDays, classesOn, includes, discountOn, discountPercent,
    discountScope, discountIds, publish, publishDrafts, free, digitalOn, digitalIds,
  })
  const [baseline] = useState(() => shape)
  const dirty = shape !== baseline

  /* El descuento es de las membresías: un paquete son créditos, no una
     relación, y sólo puede dar clases. */
  const canDiscount = kind === 'membership' && Boolean(catalog.memberDiscountReady)
  const isFree = free && canDiscount
  const canDigital = Boolean(catalog.digitalReady)
  const classing = classesOn && !isFree
  const digitalizing = canDigital && digitalOn
  const steps = ALL_STEPS
  const stepIndex = Math.max(0, steps.findIndex((row) => row.key === step))

  const number = (value: string) => {
    const parsed = Number.parseInt(value.replace(/\D/g, ''), 10)
    return Number.isFinite(parsed) && parsed > 0 ? Math.min(parsed, 1000) : null
  }
  const amountMinor = isFree ? 0 : toMinorUnits(amount, currency)
  const percentValue = Math.min(number(discountPercent) ?? 0, 90)
  const titleOf = new Map(catalog.bookings.map((booking) => [booking.offerId, booking.title]))
  const discountables = catalog.discountables ?? []
  const shownBookings = catalog.bookings.filter((booking) => booking.status !== 'archived' && matchesSearch(booking.title, query))
  const digitals = catalog.digitals ?? []
  const digitalTitleOf = new Map(digitals.map((offer) => [offer.offerId, offer.title]))
  const shownDigitals = digitals.filter((offer) => offer.status !== 'archived' && matchesSearch(offer.title, digitalQuery))
  const shownDiscountables = discountables.filter((offer) => offer.status !== 'archived' && matchesSearch(offer.title, discountQuery))

  /** Lo que falta en un paso, o null si está completo. */
  const problemIn = (key: StepKey): typeof invalid => {
    if (key === 'info') return title.trim() ? null : 'title'
    if (key === 'access') {
      if (isFree) return null
      if (amountMinor === null || amountMinor <= 0) return 'amount'
      return kind === 'pack' && expires && !number(validDays) ? 'rule' : null
    }
    if (key === 'benefits') {
      const discounting = canDiscount && discountOn
      if (!classing && !digitalizing && !discounting) return 'benefits'
      if (classing && includes.length === 0) return 'classes'
      if (classing && (kind === 'pack' ? !number(credits) : !(unlimited || number(perPeriod)))) return 'credits'
      if (digitalizing && digitalIds.length === 0) return 'digital'
      if (discounting && !(percentValue >= 1 && (discountScope === 'all' || discountIds.length > 0))) return 'discount'
    }
    return null
  }
  const firstProblem = steps.find((row) => problemIn(row.key) !== null)?.key ?? null

  /*
   * Qué da publicado. Una membresía publicada que no da nada publicado sería
   * vender algo vacío: eso no se deja. Clases en borrador sí (una no debería
   * impedir vender), pero se avisa y se ofrece publicarlas a la vez.
   */
  const statusOf = new Map(catalog.bookings.map((booking) => [booking.offerId, booking.status]))
  const included = classing ? includes : []
  const givenDigital = digitalizing ? digitalIds : []
  const draftIncluded = included.filter((id) => statusOf.get(id) === 'draft')
  const liveIncluded = included.filter((id) => statusOf.get(id) === 'active')
  const liveDiscounted = canDiscount && discountOn && percentValue >= 1
    && (discountScope === 'all'
      ? discountables.some((offer) => offer.status === 'active')
      : discountables.some((offer) => offer.status === 'active' && discountIds.includes(offer.offerId)))
  const givesSomething = liveIncluded.length > 0 || (publishDrafts && draftIncluded.length > 0) || liveDiscounted || givenDigital.length > 0
  const publishBlocked = publish && !givesSomething

  const goTo = (key: StepKey) => { setInvalid(null); setError(''); setStep(key) }
  const next = () => {
    const problem = problemIn(step)
    if (problem) { setInvalid(problem); return }
    const following = steps[stepIndex + 1]
    if (following) goTo(following.key)
  }

  // ── El raíl: la pastilla bajo el paso actual, medida (los nombres no miden igual).
  const railRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const rail = railRef.current
    if (!rail) return
    const place = () => {
      const active = rail.querySelector<HTMLButtonElement>('button.is-current')
      if (!active) return
      rail.style.setProperty('--step-x', `${active.offsetLeft}px`)
      rail.style.setProperty('--step-w', `${active.offsetWidth}px`)
    }
    place()
    const observer = new ResizeObserver(place)
    observer.observe(rail)
    return () => observer.disconnect()
  }, [step, steps])
  const wizardRef = useRef<HTMLDivElement>(null)
  useScrollTopOnStep(step, wizardRef)

  const save = async () => {
    if (firstProblem) { goTo(firstProblem); setInvalid(problemIn(firstProblem)); return }
    if (amountMinor === null) return
    if (publishBlocked) { goTo('review'); return }
    setSaving(true)
    setError('')
    try {
      const wantedInterval: OfferPrice['interval'] = kind === 'membership' ? chosenCadence.interval : 'one_time'
      const wantedCount = kind === 'membership' ? chosenCadence.count : 1
      let offerId = item?.offerId ?? ''
      if (!item) {
        const created = await createOffer(projectId, {
          title: title.trim(),
          type: 'service',
          ...(description.trim() ? { shortDescription: description.trim(), description: description.trim() } : {}),
          ...(images.length ? { images } : {}),
          price: { unitAmount: amountMinor, currency, interval: wantedInterval, intervalCount: wantedCount },
        }, token)
        offerId = created.id
      } else {
        const updated = await updateOffer(projectId, item.offerId, {
          title: title.trim(),
          ...(description.trim() ? { shortDescription: description.trim() } : {}),
          images,
        }, token)
        /* Otro precio u otra cadencia: un precio nuevo (Stripe no cambia los que
           ya existen; quien ya pagaba sigue con el suyo) y el anterior fuera. */
        const current = updated.prices.find((price) => price.active && price.isDefault) ?? updated.prices.find((price) => price.active)
        if (!current || current.unitAmount !== amountMinor || current.interval !== wantedInterval || (current.intervalCount || 1) !== wantedCount) {
          await addOfferPrice(projectId, item.offerId, { unitAmount: amountMinor, currency, interval: wantedInterval, intervalCount: wantedCount, isDefault: true }, token)
          if (current) await removeOfferPrice(projectId, item.offerId, current.id, token).catch(() => undefined)
        }
      }
      await saveMembership(projectId, offerId, {
        bookingsPerPeriod: kind === 'membership' && classing ? (unlimited ? null : number(perPeriod)) : null,
        packCredits: kind === 'pack' && classing ? number(credits) : null,
        packValidDays: kind === 'pack' && expires ? number(validDays) : null,
        includes: included,
        ...(canDigital ? { digitalIncludes: givenDigital } : {}),
        ...(catalog.memberDiscountReady
          ? {
            memberDiscountPercent: canDiscount && discountOn ? percentValue : null,
            memberDiscountOfferIds: canDiscount && discountOn && discountScope === 'some' ? discountIds : null,
          }
          : {}),
      }, token)
      /* Las clases en borrador, antes que la membresía: así, cuando ésta sale
         a la venta, lo que incluye ya se puede reservar. Una que no se pueda
         publicar (le falta algo) no frena lo demás: se dice y se sigue. */
      if (publish && publishDrafts) {
        for (const bookingId of draftIncluded) {
          try {
            await updateOffer(projectId, bookingId, { status: 'active' }, token)
          } catch (draftError) {
            showToast(translateServerError(draftError, t('memberships.wizard.publishDraftFailed', { title: titleOf.get(bookingId) ?? '' })), 'error')
          }
        }
      }
      const wantedStatus = publish ? 'active' : 'draft'
      if ((item?.status ?? 'draft') !== wantedStatus) {
        try {
          await updateOffer(projectId, offerId, { status: wantedStatus }, token)
        } catch (publishError) {
          /* Guardada en borrador: publicar puede pedir Stripe conectado. */
          showToast(translateServerError(publishError, t('memberships.publishFailed')), 'error')
        }
      }
      showToast(t('memberships.saved'))
      onSaved(offerId)
    } catch (saveError) {
      setError(translateServerError(saveError, t('memberships.saveFailed')))
      setSaving(false)
    }
  }

  // ── Lo que dice la vista previa y el repaso.
  const priceLabel = isFree ? t('memberships.wizard.free') : amountMinor !== null && amountMinor > 0
    ? kind === 'pack'
      ? formatMoney(amountMinor, currency)
      : `${formatMoney(amountMinor, currency)} · ${t(chosenCadence.labelKey).toLocaleLowerCase()}`
    : ''
  const ruleLabel = !classing ? '' : kind === 'pack'
    ? (number(credits)
      ? t(expires && number(validDays) ? 'memberships.packRuleDays' : 'memberships.packRule', { count: number(credits) ?? 0, days: number(validDays) ?? 0 })
      : '')
    : unlimited ? t('memberships.unlimited') : t('memberships.perPeriod', { count: number(perPeriod) ?? 0 })
  const includedTitles = included.map((id) => titleOf.get(id) ?? '').filter(Boolean)
  const digitalTitles = givenDigital.map((id) => digitalTitleOf.get(id) ?? '').filter(Boolean)
  const reviewTitles = included
    .filter((id) => titleOf.has(id))
    .map((id) => `${titleOf.get(id)}${statusOf.get(id) === 'draft' ? ` (${t('collections.form.draft')})` : ''}`)
  const discountLabel = canDiscount && discountOn && percentValue >= 1
    ? t(discountScope === 'some' ? 'memberships.discountSome' : 'memberships.discountAll', { percent: percentValue, count: discountIds.length })
    : ''

  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((row) => row !== id) : [...list, id])

  return createPortal(
    <div
      ref={wizardRef}
      className="dash-svc-wizard dash-ofw dash-membership-modal"
      role="dialog"
      aria-modal="true"
      aria-label={t(editing ? 'memberships.form.editTitle' : 'memberships.form.newTitle')}
    >
      <WizardTopBar
        onBack={onClose}
        center={(
          <nav className="dash-svc-wizard-progress" aria-label={t('offers.new.progressAria')}>
            <div className="dash-svc-wizard-rail" ref={railRef}>
              <span className="dash-svc-wizard-rail-thumb" aria-hidden="true" />
              {/* Creando, sólo hacia atrás; editando, cualquier paso: se entra a
                  cambiar una cosa concreta, como en el asistente de ofertas. */}
              {steps.map(({ key, labelKey, Icon }, index) => {
                const reachable = editing || index < stepIndex
                return (
                  <button
                    type="button"
                    key={key}
                    className={`${index === stepIndex ? 'is-current' : ''}${index < stepIndex ? ' is-done' : ''}`}
                    disabled={!reachable && index !== stepIndex}
                    onClick={() => { if (reachable) goTo(key) }}
                  >
                    <span>{!editing && index < stepIndex ? <IconCheck size={13} /> : <Icon size={14} />}</span>
                    <em>{t(labelKey)}</em>
                  </button>
                )
              })}
            </div>
          </nav>
        )}
        {...(editing && dirty ? {
          closeHidden: true,
          actions: (
            <PendingChangesBar
              saving={saving}
              discardLabel={t('offers.new.cancel')}
              saveLabel={t('offers.edit.save')}
              savingLabel={t('offers.edit.saving')}
              onDiscard={onClose}
              onSave={() => { void save() }}
            />
          ),
        } : {})}
        onClose={onClose}
        busy={saving}
      />

      <div className="dash-svc-wizard-body">
        <form
          className="dash-svc-wizard-form"
          onSubmit={(event) => {
            event.preventDefault()
            if (stepIndex < steps.length - 1) next(); else void save()
          }}
        >
          <div className="dash-svc-wizard-form-inner">
            {/* ── 1. Qué es ─────────────────────────────────────────────── */}
            {step === 'info' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t('memberships.wizard.info.kicker')}</span>
                <h2>{t('memberships.wizard.info.title')}</h2>
                <p>{t('memberships.wizard.info.lead')}</p>

                <div className="dash-svc-group">
                  <span className="dash-svc-group-label">{t('memberships.form.type')}</span>
                  <div className="dash-membership-kinds" role="radiogroup" aria-label={t('memberships.form.type')}>
                    {(['membership', 'pack'] as const).map((value) => (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={kind === value}
                        aria-disabled={kindLocked && kind !== value ? true : undefined}
                        className={`${kind === value ? 'is-on' : ''}${kindLocked && kind !== value ? ' is-locked' : ''}`}
                        onClick={() => { if (kindLocked && kind !== value) setWhyKindLocked(true); else setKind(value) }}
                      >
                        <span className="dash-membership-kind-icon" aria-hidden="true">{value === 'membership' ? <IconRepeat size={16} /> : <IconTicket size={16} />}</span>
                        <span>
                          <strong>{t(`memberships.kind.${value}`)}</strong>
                          <small>{t(`memberships.form.${value}Hint`)}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="dash-svc-group">
                  <label className={`dash-svc-field${invalid === 'title' ? ' is-invalid' : ''}`}>
                    <span>{t('memberships.form.name')} <b>*</b></span>
                    <input
                      autoFocus={!editing}
                      maxLength={120}
                      value={title}
                      placeholder={t(kind === 'pack' ? 'memberships.form.namePackPlaceholder' : 'memberships.form.namePlaceholder')}
                      aria-invalid={invalid === 'title' || undefined}
                      onChange={(event) => { setTitle(event.target.value); if (invalid === 'title') setInvalid(null) }}
                    />
                    {invalid === 'title' ? <small className="dash-svc-field-error">{t('memberships.wizard.needName')}</small> : null}
                  </label>
                  <label className="dash-svc-field">
                    <span>{t('memberships.form.description')}</span>
                    <textarea value={description} maxLength={200} rows={3} placeholder={t('memberships.form.descriptionPlaceholder')} onChange={(event) => setDescription(event.target.value)} />
                    <small>{t('memberships.wizard.descriptionFoot')}</small>
                  </label>
                </div>

                {/* Las fotos con el mismo campo que las demás ofertas: del
                    almacén del panel, arrastrar para ordenar, la primera es la
                    portada. */}
                <div className="dash-svc-group">
                  <div className="dash-svc-image-field">
                    <div><strong>{t('gallery.label')}</strong><span>{t('offers.new.optional')}</span></div>
                    <OfferGalleryField token={token} projectId={projectId} images={images} onChange={setImages} />
                  </div>
                </div>
              </section>
            ) : null}

            {/* ── 2. Precio y lo que da ──────────────────────────────────── */}
            {step === 'access' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t('memberships.wizard.access.kicker')}</span>
                <h2>{t(kind === 'pack' ? 'memberships.wizard.access.titlePack' : 'memberships.wizard.access.title')}</h2>
                <p>{t(`memberships.form.${kind}Hint`)}</p>

                {/* Precio y cada cuánto, en UNA tarjeta: son la misma frase,
                    «$55 cada mes». Gratis (sólo membresías) quita las dos. */}
                <div className="dash-svc-setting-block">
                  <label htmlFor="msw-amount">{t('memberships.form.price')} <b>*</b></label>
                  {canDiscount ? (
                    <div className={`dash-svc-segmented${isFree ? ' is-second' : ''}`} role="radiogroup" aria-label={t('memberships.wizard.priceMode')}>
                      <button type="button" role="radio" aria-checked={!isFree} className={isFree ? '' : 'is-on'} onClick={() => setFree(false)}>{t('memberships.wizard.paid')}</button>
                      <button type="button" role="radio" aria-checked={isFree} className={isFree ? 'is-on' : ''} onClick={() => { setFree(true); setDiscountOn(true); setInvalid(null) }}>{t('memberships.wizard.free')}</button>
                    </div>
                  ) : null}
                  {isFree ? (
                    <span className="dash-svc-block-hint">{t('memberships.wizard.freeHint')}</span>
                  ) : (
                    <div className="dash-mshipw-price-row">
                      <div className={`dash-ofw-amount${invalid === 'amount' ? ' is-invalid' : ''}`}>
                        <CurrencySymbol currency={currency} />
                        <input id="msw-amount" inputMode="decimal" value={amount} placeholder={kind === 'pack' ? '1200' : '600'} onChange={(event) => { setAmount(event.target.value); if (invalid === 'amount') setInvalid(null) }} />
                        <span className="dash-ofw-amount-currency">{currency.toUpperCase()}</span>
                      </div>
                      {kind === 'membership' ? (
                        <select className="dash-mshipw-cadence" value={cadence} aria-label={t('memberships.form.billing')} onChange={(event) => setCadence(event.target.value as CadenceId)}>
                          {MEMBERSHIP_CADENCES.map((id) => <option key={id} value={id}>{t(cadenceOf(id).labelKey)}</option>)}
                        </select>
                      ) : null}
                    </div>
                  )}
                  {invalid === 'amount' ? <small className="dash-svc-field-error">{t('memberships.wizard.needPrice')}</small> : null}
                </div>

                {/* La vigencia del paquete: vale para sus reservas y para sus
                    productos digitales. Una membresía dura lo que se pague. */}
                {kind === 'pack' ? (
                  <div className="dash-svc-setting-block">
                    <label>{t('memberships.wizard.validity')}</label>
                    <span className="dash-svc-block-hint">{t('memberships.wizard.validityHint')}</span>
                    <div className={`dash-svc-segmented${expires ? ' is-second' : ''}`} role="radiogroup" aria-label={t('memberships.wizard.validity')}>
                      <button type="button" role="radio" aria-checked={!expires} className={expires ? '' : 'is-on'} onClick={() => setExpires(false)}>{t('svcw.membership.noExpiry')}</button>
                      <button type="button" role="radio" aria-checked={expires} className={expires ? 'is-on' : ''} onClick={() => setExpires(true)}>{t('memberships.form.expiresShort')}</button>
                    </div>
                    {expires ? (
                      <div className={`dash-ofw-amount${invalid === 'rule' ? ' is-invalid' : ''}`}>
                        <input inputMode="numeric" value={validDays} aria-label={t('svcw.membership.days')} onChange={(event) => setValidDays(event.target.value)} />
                        <span className="dash-ofw-amount-currency">{t('memberships.form.daysUnit')}</span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {invalid === 'rule' ? <small className="dash-svc-field-error">{t('memberships.wizard.needRule')}</small> : null}
              </section>
            ) : null}

            {/* ── 3. Qué incluye: clases, descuento o las dos ─────────────── */}
            {step === 'benefits' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t('memberships.wizard.benefits.kicker')}</span>
                <h2>{t('memberships.wizard.benefits.title')}</h2>
                <p>{t('memberships.wizard.benefits.lead')}</p>

                {/* Gratis, las clases se ven pero no se encienden: serían clases
                    gratis sin límite. Pulsar el interruptor explica por qué. */}
                {isFree ? (
                  <div className="dash-mshipw-benefit is-locked">
                    <div className="dash-membership-toggle">
                      <span>
                        <h3>{t('memberships.wizard.benefits.classes')}</h3>
                        <small>{t('memberships.wizard.benefits.classesFree')}</small>
                      </span>
                      <button type="button" role="switch" aria-checked={false} aria-disabled="true" aria-label={t('memberships.wizard.benefits.classes')} className="cwa-switch" onClick={() => setWhyNoClasses(true)}>
                        <span />
                      </button>
                    </div>
                  </div>
                ) : (
                <div className={`dash-mshipw-benefit${classesOn ? ' is-on' : ''}`}>
                  <div className="dash-membership-toggle">
                    <span>
                      <h3>{t('memberships.wizard.benefits.classes')}</h3>
                      <small>{t('memberships.wizard.benefits.classesHint')}</small>
                    </span>
                    <button type="button" role="switch" aria-checked={classesOn} aria-label={t('memberships.wizard.benefits.classes')} className={`cwa-switch${classesOn ? ' on' : ''}`} onClick={() => { setClassesOn(!classesOn); setInvalid(null) }}>
                      <span />
                    </button>
                  </div>
                  {classesOn ? (
                    <>
                    {kind === 'membership' ? (
                      <div className="dash-svc-setting-block">
                        <label>{t('svcw.membership.perPeriod')}</label>
                        <span className="dash-svc-block-hint">{t('memberships.wizard.perPeriodHint')}</span>
                        <div className={`dash-svc-segmented${unlimited ? '' : ' is-second'}`} role="radiogroup" aria-label={t('svcw.membership.perPeriod')}>
                          <button type="button" role="radio" aria-checked={unlimited} className={unlimited ? 'is-on' : ''} onClick={() => setUnlimited(true)}>{t('svcw.membership.unlimited')}</button>
                          <button type="button" role="radio" aria-checked={!unlimited} className={unlimited ? '' : 'is-on'} onClick={() => setUnlimited(false)}>{t('memberships.form.limited')}</button>
                        </div>
                        {unlimited ? null : (
                          <div className={`dash-ofw-amount${invalid === 'credits' ? ' is-invalid' : ''}`}>
                            <input inputMode="numeric" value={perPeriod} aria-label={t('memberships.wizard.howMany')} onChange={(event) => setPerPeriod(event.target.value)} />
                            <span className="dash-ofw-amount-currency">{t(chosenCadence.id === 'month' ? 'memberships.form.perMonth' : chosenCadence.id === 'year' ? 'memberships.form.perYear' : 'memberships.form.perCycle')}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="dash-svc-setting-block">
                        <label>{t('memberships.wizard.howMany')}</label>
                        <div className={`dash-ofw-amount${invalid === 'credits' ? ' is-invalid' : ''}`}>
                          <input inputMode="numeric" value={credits} aria-label={t('svcw.membership.credits')} onChange={(event) => setCredits(event.target.value)} />
                          <span className="dash-ofw-amount-currency">{t('memberships.form.classesUnit')}</span>
                        </div>
                      </div>
                    )}
                    {invalid === 'credits' ? <small className="dash-svc-field-error">{t('memberships.wizard.needCredits')}</small> : null}
                    {catalog.bookings.length === 0 ? (
                      <p className="dash-membership-empty">{t('memberships.wizard.benefits.noClasses')}</p>
                    ) : (
                      <>
                  <div className="dash-collection-offers">
                    {catalog.bookings.length > 6 ? (
                      <span className="dash-collection-search">
                        <IconSearch size={14} aria-hidden="true" />
                        <input value={query} placeholder={t('collections.form.search')} onChange={(event) => setQuery(event.target.value)} />
                      </span>
                    ) : null}
                    <ul className="dash-collection-offer-list">
                      {shownBookings.map((booking) => {
                        const on = includes.includes(booking.offerId)
                        return (
                          <li key={booking.offerId}>
                            <label className={on ? 'is-on' : ''}>
                              <input type="checkbox" checked={on} onChange={() => setIncludes((current) => toggle(current, booking.offerId))} />
                              <span className="dash-collection-thumb" aria-hidden="true">{booking.image ? <img src={booking.image} alt="" loading="lazy" /> : <IconCalendarEvent size={14} />}</span>
                              <span className="dash-collection-offer-copy">
                                <strong>{booking.title}</strong>
                                <small>{booking.status !== 'active' ? t('memberships.wizard.draftLabel') : t('memberships.form.bookingKind')}</small>
                              </span>
                            </label>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                        <small className="dash-mshipw-count">{t('collections.form.picked', { count: includes.length })}</small>
                      </>
                    )}
                    </>
                  ) : null}
                  {invalid === 'classes' ? <small className="dash-svc-field-error">{t('memberships.wizard.needClasses')}</small> : null}
                </div>
                )}

                {canDigital ? (
                  <div className={`dash-mshipw-benefit${digitalOn ? ' is-on' : ''}`}>
                    <div className="dash-membership-toggle">
                      <span>
                        <h3>{t('memberships.wizard.benefits.digital')}</h3>
                        <small>{t(kind === 'pack' ? 'memberships.wizard.benefits.digitalHintPack' : 'memberships.wizard.benefits.digitalHintMembership')}</small>
                      </span>
                      <button type="button" role="switch" aria-checked={digitalOn} aria-label={t('memberships.wizard.benefits.digital')} className={`cwa-switch${digitalOn ? ' on' : ''}`} onClick={() => { setDigitalOn(!digitalOn); setInvalid(null) }}>
                        <span />
                      </button>
                    </div>
                    {digitalOn ? (
                      digitals.length === 0 ? (
                        <p className="dash-membership-empty">{t('memberships.wizard.benefits.noDigital')}</p>
                      ) : (
                        <>
                          <div className="dash-collection-offers">
                            {digitals.length > 6 ? (
                              <span className="dash-collection-search">
                                <IconSearch size={14} aria-hidden="true" />
                                <input value={digitalQuery} placeholder={t('collections.form.search')} onChange={(event) => setDigitalQuery(event.target.value)} />
                              </span>
                            ) : null}
                            <ul className="dash-collection-offer-list">
                              {shownDigitals.map((offer) => {
                                const on = digitalIds.includes(offer.offerId)
                                return (
                                  <li key={offer.offerId}>
                                    <label className={on ? 'is-on' : ''}>
                                      <input type="checkbox" checked={on} onChange={() => setDigitalIds((current) => toggle(current, offer.offerId))} />
                                      <span className="dash-collection-thumb" aria-hidden="true">{offer.image ? <img src={offer.image} alt="" loading="lazy" /> : <IconDownload size={14} />}</span>
                                      <span className="dash-collection-offer-copy">
                                        <strong>{offer.title}</strong>
                                        <small>{offer.status !== 'active' ? t('memberships.wizard.draftLabel') : t('memberships.wizard.digitalKind')}</small>
                                      </span>
                                    </label>
                                  </li>
                                )
                              })}
                            </ul>
                          </div>
                          <small className="dash-mshipw-count">{t('collections.form.picked', { count: digitalIds.length })}</small>
                        </>
                      )
                    ) : null}
                    {invalid === 'digital' ? <small className="dash-svc-field-error">{t('memberships.wizard.needDigital')}</small> : null}
                  </div>
                ) : null}

                {canDiscount ? (
                  <div className={`dash-mshipw-benefit${discountOn ? ' is-on' : ''}`}>
                    <div className="dash-membership-toggle">
                      <span>
                        <h3>{t('memberships.form.discount')}</h3>
                        <small>{t('memberships.wizard.benefits.discountHint')}</small>
                      </span>
                      <button type="button" role="switch" aria-checked={discountOn} aria-label={t('memberships.form.discount')} className={`cwa-switch${discountOn ? ' on' : ''}`} onClick={() => { setDiscountOn(!discountOn); setInvalid(null) }}>
                        <span />
                      </button>
                    </div>
                    {discountOn ? (
                      <>
                    <div className="dash-svc-setting-block">
                      <label>{t('memberships.form.discountPercent')}</label>
                      <div className={`dash-ofw-amount${invalid === 'discount' && percentValue < 1 ? ' is-invalid' : ''}`}>
                        <input inputMode="numeric" value={discountPercent} placeholder="10" aria-label={t('memberships.form.discountPercent')} onChange={(event) => setDiscountPercent(event.target.value)} />
                        <span className="dash-ofw-amount-currency">%</span>
                      </div>
                    </div>
                    <div className="dash-svc-setting-block">
                      <label>{t('memberships.form.discountScope')}</label>
                      <div className={`dash-svc-segmented${discountScope === 'some' ? ' is-second' : ''}`} role="radiogroup" aria-label={t('memberships.form.discountScope')}>
                        <button type="button" role="radio" aria-checked={discountScope === 'all'} className={discountScope === 'all' ? 'is-on' : ''} onClick={() => setDiscountScope('all')}>{t('memberships.form.discountAll')}</button>
                        <button type="button" role="radio" aria-checked={discountScope === 'some'} className={discountScope === 'some' ? 'is-on' : ''} onClick={() => setDiscountScope('some')}>{t('memberships.form.discountSome')}</button>
                      </div>
                    </div>
                    {discountScope === 'some' ? (
                      discountables.length === 0 ? (
                        <p className="dash-membership-empty">{t('memberships.form.discountNone')}</p>
                      ) : (
                        <div className="dash-collection-offers">
                          {discountables.length > 6 ? (
                            <span className="dash-collection-search">
                              <IconSearch size={14} aria-hidden="true" />
                              <input value={discountQuery} placeholder={t('collections.form.search')} onChange={(event) => setDiscountQuery(event.target.value)} />
                            </span>
                          ) : null}
                          <ul className="dash-collection-offer-list">
                            {shownDiscountables.map((offer) => {
                              const on = discountIds.includes(offer.offerId)
                              return (
                                <li key={offer.offerId}>
                                  <label className={on ? 'is-on' : ''}>
                                    <input type="checkbox" checked={on} onChange={() => setDiscountIds((current) => toggle(current, offer.offerId))} />
                                    <span className="dash-collection-thumb" aria-hidden="true">{offer.image ? <img src={offer.image} alt="" loading="lazy" /> : <IconTag size={14} />}</span>
                                    <span className="dash-collection-offer-copy">
                                      <strong>{offer.title}</strong>
                                      {offer.status !== 'active' ? <small>{t('memberships.wizard.draftLabel')}</small> : null}
                                    </span>
                                  </label>
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      )
                    ) : null}
                      </>
                    ) : null}
                    {invalid === 'discount' ? <small className="dash-svc-field-error">{t('memberships.wizard.needDiscount')}</small> : null}
                  </div>
                ) : null}

                {invalid === 'benefits' ? <p className="dash-svc-field-error" role="alert">{t('memberships.wizard.needBenefit')}</p> : null}
              </section>
            ) : null}

            {/* ── 5. Repaso ──────────────────────────────────────────────── */}
            {step === 'review' ? (
              <section className="dash-svc-panel-step">
                <span className="dash-svc-step-kicker">{t('offers.new.review.kicker')}</span>
                <h2>{t('memberships.wizard.review.title')}</h2>
                <p>{t('offers.new.review.lead')}</p>
                <div className="dash-svc-review">
                  <div>
                    <span>{t('memberships.form.name')}</span>
                    <strong>{title.trim() || <em>{t('offers.new.review.noTitle')}</em>}</strong>
                    <button type="button" onClick={() => goTo('info')}>{t('offers.new.review.edit')}</button>
                  </div>
                  <div>
                    <span>{t('memberships.form.type')}</span>
                    <strong>{t(`memberships.kind.${kind}`)}</strong>
                    <button type="button" onClick={() => goTo('info')}>{t('offers.new.review.edit')}</button>
                  </div>
                  <div>
                    <span>{t('memberships.form.price')}</span>
                    <strong>{priceLabel || <em>{t('offers.noPrice')}</em>}</strong>
                    <button type="button" onClick={() => goTo('access')}>{t('offers.new.review.edit')}</button>
                  </div>
                  <div>
                    <span>{t(kind === 'pack' ? 'svcw.membership.credits' : 'svcw.membership.perPeriod')}</span>
                    <strong>{ruleLabel || <em>{t('common.none')}</em>}</strong>
                    <button type="button" onClick={() => goTo('benefits')}>{t('offers.new.review.edit')}</button>
                  </div>
                  <div>
                    <span>{t('memberships.form.includes')}</span>
                    <strong className="dash-svc-review-excerpt">{reviewTitles.length ? reviewTitles.join(' · ') : <em>{t('memberships.includesNone')}</em>}</strong>
                    <button type="button" onClick={() => goTo('benefits')}>{t('offers.new.review.edit')}</button>
                  </div>
                  {canDigital ? (
                    <div>
                      <span>{t('memberships.wizard.review.digital')}</span>
                      <strong className="dash-svc-review-excerpt">{digitalTitles.length ? digitalTitles.join(' · ') : <em>{t('common.none')}</em>}</strong>
                      <button type="button" onClick={() => goTo('benefits')}>{t('offers.new.review.edit')}</button>
                    </div>
                  ) : null}
                  {canDiscount ? (
                    <div>
                      <span>{t('memberships.form.discount')}</span>
                      <strong>{discountLabel || <em>{t('common.none')}</em>}</strong>
                      <button type="button" onClick={() => goTo('benefits')}>{t('offers.new.review.edit')}</button>
                    </div>
                  ) : null}
                </div>
                <div className="dash-svc-setting-block dash-svc-publish-block">
                  <label id="msw-publish-label">{t(editing ? 'offers.edit.onSave' : 'offers.new.review.onCreate')}</label>
                  <div className={`dash-svc-segmented${publish ? ' is-second' : ''}`} role="radiogroup" aria-labelledby="msw-publish-label">
                    <button type="button" role="radio" aria-checked={!publish} className={!publish ? 'is-on' : ''} disabled={saving} onClick={() => setPublish(false)}>
                      <IconPencil size={14} aria-hidden="true" /> {t('offers.status.draft')}
                    </button>
                    <button type="button" role="radio" aria-checked={publish} className={publish ? 'is-on' : ''} disabled={saving} onClick={() => setPublish(true)}>
                      <IconWorld size={14} aria-hidden="true" /> {t('offers.status.active')}
                    </button>
                  </div>
                  {!publish ? <small>{t('offers.new.review.draftHint')}</small> : null}
                </div>
                {publish && draftIncluded.length > 0 ? (
                  <div className="dash-mshipw-warn">
                    <p>{t(draftIncluded.length === 1 ? 'memberships.wizard.review.draftWarnOne' : 'memberships.wizard.review.draftWarn', { count: draftIncluded.length })}</p>
                    <label>
                      <input type="checkbox" checked={publishDrafts} onChange={(event) => setPublishDrafts(event.target.checked)} />
                      <span>{t(draftIncluded.length === 1 ? 'memberships.wizard.review.publishDraftsOne' : 'memberships.wizard.review.publishDrafts')}</span>
                    </label>
                  </div>
                ) : null}
                {publishBlocked ? <p className="dash-svc-field-error" role="alert">{t('memberships.wizard.review.empty')}</p> : null}
              </section>
            ) : null}

            {error ? <p className="dash-svc-error" role="alert">{error}</p> : null}

            <div className="dash-svc-step-actions">
              {stepIndex > 0
                ? <button type="button" className="dash-secondary-btn" disabled={saving} onClick={() => goTo(steps[stepIndex - 1]?.key ?? 'info')}><IconArrowLeft size={14} /> {t('offers.new.back')}</button>
                : !editing ? <button type="button" className="dash-secondary-btn" disabled={saving} onClick={onClose}>{t('offers.new.cancel')}</button> : null}
              {stepIndex < steps.length - 1 ? (
                <button type="button" className={editing ? 'dash-secondary-btn' : 'dash-primary-btn'} disabled={saving} onClick={next}>
                  {t('offers.new.nextStep')} <IconArrowRight size={14} />
                </button>
              ) : !editing ? (
                <button type="button" className="dash-primary-btn" disabled={saving || publishBlocked} onClick={() => { void save() }}>
                  {saving ? <IconLoader2 className="dash-spin" size={14} /> : <IconCheck size={14} />}
                  {t(saving ? 'offers.new.creating' : 'memberships.form.create')}
                </button>
              ) : null}
            </div>
          </div>
        </form>

        {/* La vista previa: la tarjeta que ve el cliente, con lo que da. No
            maqueta la página de pago de Stripe (allí sólo saldrían nombre y
            precio); lo que se decide aquí es qué incluye. */}
        <aside className="dash-svc-wizard-preview" aria-label={t('offers.new.previewAria')}>
          <div className="dash-svc-preview-heading">
            <span>{t('offers.new.preview')}</span>
            <em>{t('memberships.wizard.previewHint')}</em>
          </div>
          <article className="dash-mshipw-card">
            <div className="dash-mshipw-card-cover">
              {image ? <img src={image} alt="" /> : <span aria-hidden="true">{kind === 'membership' ? <IconRepeat size={26} /> : <IconTicket size={26} />}</span>}
            </div>
            <div className="dash-mshipw-card-body">
              <span className="dash-mshipw-card-kind">{t(`memberships.kind.${kind}`)}</span>
              {title.trim() ? <strong>{title.trim()}</strong> : <span className="dash-svc-ghost is-title" aria-hidden="true" />}
              {description.trim() ? <p>{description.trim()}</p> : null}
              <span className="dash-mshipw-card-price">{priceLabel || <span className="dash-svc-ghost" aria-hidden="true" />}</span>
              <ul>
                {ruleLabel ? <li><IconCheck size={14} aria-hidden="true" />{ruleLabel}</li> : null}
                {includedTitles.slice(0, 5).map((name) => <li key={name}><IconCalendarEvent size={14} aria-hidden="true" />{name}</li>)}
                {includedTitles.length > 5 ? <li className="is-more">{t('memberships.wizard.more', { count: includedTitles.length - 5 })}</li> : null}
                {digitalTitles.slice(0, 4).map((name) => <li key={`d-${name}`}><IconDownload size={14} aria-hidden="true" />{name}</li>)}
                {discountLabel ? <li><IconDiscount size={14} aria-hidden="true" />{discountLabel}</li> : null}
              </ul>
              <span className="dash-mshipw-card-cta">{t(kind === 'pack' ? 'memberships.wizard.ctaPack' : 'memberships.wizard.cta')}</span>
            </div>
          </article>
        </aside>
      </div>

      {whyKindLocked ? (
        <ConfirmDialog
          overWizard
          informative
          title={t('memberships.wizard.kindLockedTitle')}
          message={t('memberships.wizard.kindLockedBody')}
          confirmLabel={t('memberships.wizard.whyOk')}
          onConfirm={() => setWhyKindLocked(false)}
          onCancel={() => setWhyKindLocked(false)}
        />
      ) : null}
      {whyNoClasses ? (
        <ConfirmDialog
          overWizard
          informative
          title={t('memberships.wizard.whyTitle')}
          message={t('memberships.wizard.whyBody')}
          confirmLabel={t('memberships.wizard.whyOk')}
          onConfirm={() => setWhyNoClasses(false)}
          onCancel={() => setWhyNoClasses(false)}
        />
      ) : null}
    </div>,
    document.body,
  )
}
